import { useCallback, useEffect, useState } from "react";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Pencil, Trash2, ExternalLink, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import EventEditor from "@/pages/_EventEditor";

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("approved");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/events", {
      params: { status: status === "all" ? undefined : status, q: q || undefined },
    });
    setEvents(r.data);
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleFeature = async (id, current) => {
    try {
      const r = await api.post(`/admin/events/${id}/feature`, { featured: !current });
      // Update locally to avoid full reload
      setEvents(prev => prev.map(x => x.id === id ? { ...x, featured: r.data.featured } : x));
      toast.success(r.data.featured ? "★ Featured on homepage" : "Un-featured");
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#1A3C5A]">All events</h1>
        <div className="text-sm text-slate-500">{events.length}</div>
      </div>
      <div className="flex gap-2 mb-3">
        <Input placeholder="Search…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs bg-white" data-testid="events-search"/>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="events-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-display">
            <tr>
              <th className="py-2 px-2 text-left">When</th>
              <th className="py-2 px-2 text-left">Title</th>
              <th className="py-2 px-2 text-left">Venue · Town</th>
              <th className="py-2 px-2 text-left">Category</th>
              <th className="py-2 px-2 text-left">Status</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id} className="border-t border-border hover:bg-slate-50" data-testid={`event-row-${e.id}`}>
                <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">
                  {(() => { try { return formatInTimeZone(parseISO(e.start_datetime), "America/New_York", "MMM d, h:mm a"); } catch { return e.start_datetime; } })()}
                </td>
                <td className="py-1.5 px-2 font-medium">
                  {e.title}
                  {e.source_url && <a href={e.source_url} target="_blank" rel="noreferrer" className="ml-1"><ExternalLink size={11} className="inline"/></a>}
                </td>
                <td className="py-1.5 px-2 text-slate-600">{[e.venue, e.town].filter(Boolean).join(" · ")}</td>
                <td className="py-1.5 px-2 text-slate-600">{e.category || "—"}</td>
                <td className="py-1.5 px-2">
                  <Badge variant={e.status === "approved" ? "default" : "outline"}
                         className={e.status === "approved" ? "bg-emerald-600" : e.status === "pending" ? "border-amber-400 text-amber-700" : "border-slate-300 text-slate-500"}>
                    {e.status}
                  </Badge>
                </td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap">
                  <Button
                    size="sm" variant="ghost"
                    onClick={()=>toggleFeature(e.id, e.featured)}
                    title={e.featured ? "Un-feature" : "Feature on homepage"}
                    data-testid={`feature-btn-${e.id}`}
                  >
                    <Star size={13} className={e.featured ? "fill-amber-400 text-amber-500" : "text-slate-400"}/>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={()=>setEditing(e)}><Pencil size={13}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(e.id)} className="text-red-600"><Trash2 size={13}/></Button>
                </td>
              </tr>
            ))}
            {events.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-500">No events.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <EventEditor event={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null); load();}} />}
    </div>
  );
}
